import { cn } from "@/lib/utils"

/**
 * Phones: every dialog is a sheet docked to the bottom edge, where the thumb
 * is — the centred card was the website look the owner asked to lose
 * (2026-09-11). Dialog and AlertDialog both read this, so they cannot drift.
 *
 * - `bottom: var(--kb-inset)` rides ABOVE the on-screen keyboard
 *   (lib/keyboard-inset.ts). Without it a focused field sat under the
 *   keyboard — the reason this was held back the first time.
 * - It fits in --vv-avail, the height the keyboard leaves, and scrolls inside.
 * - It slides up and back down (no zoom, no fade); BottomSheet's curve.
 * - Only the Y axis is re-anchored. X keeps `left-1/2 -translate-x-1/2`, so a
 *   caller's `sm:max-w-*` between 640 and 767 px still gives a centred sheet.
 * - Variants win over a caller's plain `rounded-[24px]` / `p-6` /
 *   `max-h-[90dvh]` (Tailwind emits variants after plain utilities), so no
 *   call site had to change.
 */
export const PHONE_DOCK = cn(
  "max-md:top-auto max-md:bottom-[var(--kb-inset)] max-md:translate-y-0",
  "max-md:max-w-none max-md:max-h-[calc(var(--vv-avail)-env(safe-area-inset-top)-0.75rem)]",
  "max-md:rounded-b-none max-md:rounded-t-[28px] max-md:ring-0 max-md:shadow-[0_-12px_48px_-16px_rgb(0_0_0/0.45)]",
  "max-md:pb-[calc(env(safe-area-inset-bottom)+1rem)]",
  "max-md:duration-300 max-md:ease-[var(--ease-drawer)]",
  "max-md:data-open:fade-in-100 max-md:data-open:zoom-in-100 max-md:data-open:slide-in-from-bottom",
  "max-md:data-closed:fade-out-100 max-md:data-closed:zoom-out-100 max-md:data-closed:slide-out-to-bottom",
  // Reduced motion: no travel, a plain fade.
  "motion-reduce:max-md:data-open:slide-in-from-bottom-0 motion-reduce:max-md:data-closed:slide-out-to-bottom-0",
  "motion-reduce:max-md:data-open:fade-in-0 motion-reduce:max-md:data-closed:fade-out-0",
)

/** The scrim under it: the bottom sheet's, so a dialog and a sheet look alike. */
export const PHONE_DOCK_BACKDROP = "max-md:bg-black/45 max-md:backdrop-blur-none max-md:duration-300"

/** A footer running to the sheet's bottom edge, over the safe-area padding.
 *  Its buttons stack full-width (already, below sm) at a thumb's 44 px.
 *
 *  1.50 — it stays ON that edge while the sheet scrolls. With the keyboard up
 *  the sheet gets only the height the keyboard leaves; the sale's cash
 *  confirm (449 px) did not fit above a phone keyboard, scrolled inside, and
 *  „დადასტურება"/„გაუქმება" went under the fold (the owner, 10-16). Sticky
 *  measures from inside the sheet's padding, so `bottom` is that padding
 *  negated — flush with the edge, as without the scroll. The footers' own
 *  background is opaque (dialog.tsx, alert-dialog.tsx), so what scrolls
 *  under it does not show through. */
export const PHONE_DOCK_FOOTER = cn(
  "max-md:rounded-b-none max-md:-mb-[calc(env(safe-area-inset-bottom)+1rem)] max-md:pb-[calc(env(safe-area-inset-bottom)+1rem)]",
  "max-md:sticky max-md:bottom-[calc(-1*(env(safe-area-inset-bottom)+1rem))]",
  "max-md:*:min-h-11 max-md:*:text-[15px]",
)
